import { useState } from "react";
import { Download, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAsync } from "@/lib/queries";
import { get } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { money } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { blobError, downloadFile } from "@/components/ExportRunMenu";

const KIND = { plan: "Plan", plan_renewal: "Plan", text_tokens: "Texts" };

function ReceiptRow({ r }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadFile(`/billing/receipts/${r.id}/pdf`, undefined, `receipt-${r.receipt_number}.pdf`);
    } catch (e) {
      toast.error(await blobError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 py-3" data-testid={`receipt-row-${r.id}`}>
      <span className="font-data w-24 text-xs text-slate-500">{formatDate(r.paid_at.slice(0, 10), "dd/MM/yyyy")}</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{KIND[r.kind] || "Payment"}</span>
      <span className="order-last w-full text-sm text-slate-800 sm:order-none sm:w-auto sm:min-w-0 sm:flex-1 sm:truncate" data-testid={`receipt-description-${r.id}`}>{r.description}</span>
      <span className="ml-auto font-data text-sm font-semibold text-slate-900 sm:ml-0" data-testid={`receipt-amount-${r.id}`}>{money(r.amount)}</span>
      <Button size="sm" variant="outline" onClick={download} disabled={busy} data-testid={`receipt-download-${r.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}<span className="ml-1">PDF</span>
      </Button>
    </li>
  );
}

export const BillingHistory = () => {
  const { data, loading, error, reload } = useAsync(() => get("/billing/receipts"), []);
  return (
    <section className="max-w-3xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="billing-history">
      <h2 className="font-display flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg"><Receipt className="h-5 w-5 text-blue-700" /> Billing history</h2>
      <p className="mt-1 text-sm text-slate-500">Every payment made on Square, with a downloadable receipt.</p>
      {error && <p className="mt-4 text-sm text-red-700" role="alert" data-testid="billing-history-error">Couldn&apos;t load your billing history. <button type="button" className="font-semibold underline" onClick={reload} data-testid="billing-history-retry">Try again</button></p>}
      {loading && !data && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
      {data && data.length === 0 && <p className="mt-4 text-sm text-slate-500" data-testid="billing-history-empty">No Square payments yet.</p>}
      {data?.length > 0 && <ul className="mt-2 divide-y divide-slate-100">{data.map((r) => <ReceiptRow key={r.id} r={r} />)}</ul>}
    </section>
  );
};
